import { useState } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Banner,
  DataTable,
  EmptyState,
  Badge,
  SkeletonBodyText,
} from '@shopify/polaris';
import { useConversions } from '../hooks/api';
import { useLanguage } from '../i18n/LanguageContext';
import { localeMap } from '../i18n/translations';

// ============================================================================
// Live Event Debugger — polls /api/dashboard/conversions every 5s
// ============================================================================
// This screen used to consume an SSE stream, and it did not work where the
// product actually runs. Measured on 2026-07-27 against the live store:
//
//   - the server accepted the connection and wrote the snapshot immediately
//     (logged "[SSE] Client connected"),
//   - curl over HTTP/2 through Cloudflare, with browser headers, received that
//     snapshot in 128ms,
//   - plain Chrome streams a chunked response fine (6 chunks, 1/s, verified),
//   - but inside the Shopify admin iframe the whole body was withheld until the
//     server closed the stream at its 10-minute cap. The table then filled all
//     at once, ten minutes late, having shown "Connecting…" the entire time.
//
// The one wrapper unique to that context is App Bridge, which replaces
// globalThis.fetch (confirmed by reading cdn.shopify.com/shopifycloud/app-bridge.js).
// The exact mechanism inside it is unverified, and it does not change the
// decision: a debug screen must not depend on a streamed response surviving
// every proxy and every SDK in the path.
//
// Polling costs 12 requests/minute per open tab, against a 100/minute limit,
// and buys back: no per-shop connection cap that can lock a merchant out, no
// Redis subscriber per viewer, no heartbeats, no forced 10-minute reconnect,
// no backoff state machine. What it gives up is sub-second latency on a screen
// where nobody can tell 0.5s from 5s. React Query stops polling on its own when
// the tab is in the background.
//
// The "Flags" column is gone with it. Its fraud score was computed in the worker
// and never persisted — Conversion has no fraud column — so it could only ever
// arrive on a live push, and the live push never reached this screen. It has
// been an empty column since the day it shipped.
// ============================================================================

const POLL_MS = 5000;

export function EventStream() {
  const [isPaused, setIsPaused] = useState(false);
  const { language } = useLanguage();
  const { data, isLoading, isFetching, error, dataUpdatedAt } = useConversions(25, {
    refetchInterval: isPaused ? false : POLL_MS,
  });

  const conversions = data?.conversions ?? [];

  const statusBadge = () => {
    if (isPaused) return <Badge>Paused</Badge>;
    if (error) return <Badge tone="critical">Disconnected</Badge>;
    if (isFetching) return <Badge tone="info">Refreshing&hellip;</Badge>;
    return <Badge tone="success">Live</Badge>;
  };

  const statusText = () => {
    if (isPaused) return 'Click Resume to start refreshing again.';
    if (error) return (error as Error).message || 'Could not reach the server. Retrying.';
    if (dataUpdatedAt) {
      return `Refreshing every ${POLL_MS / 1000}s. Last updated ${new Date(dataUpdatedAt).toLocaleTimeString(localeMap[language])}.`;
    }
    return 'Loading recent conversions…';
  };

  return (
    <Page
      title="Live Event Debugger"
      subtitle="Conversions as they land, with the channels each one reached"
      primaryAction={{
        content: isPaused ? 'Resume' : 'Pause',
        onAction: () => setIsPaused((p) => !p),
      }}
    >
      <Layout>
        <Layout.Section>
          <Banner tone={error ? 'critical' : 'info'}>
            <InlineStack gap="200" blockAlign="center">
              <span>Connection:</span>
              {statusBadge()}
              <span style={{ color: '#637381' }}>{statusText()}</span>
            </InlineStack>
          </Banner>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <Text as="h2" variant="headingMd">
                  Recent Conversions
                </Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  {conversions.length} event{conversions.length === 1 ? '' : 's'}
                </Text>
              </InlineStack>

              {isLoading ? (
                <SkeletonBodyText lines={6} />
              ) : conversions.length === 0 ? (
                <EmptyState
                  heading="No events yet"
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                >
                  <p>
                    Place a test order in your store, or trigger a test event from a channel
                    configuration page, to see it appear here.
                  </p>
                </EmptyState>
              ) : (
                <DataTable
                  columnContentTypes={[
                    'text',
                    'text',
                    'numeric',
                    'text',
                    'text',
                    'text',
                    'text',
                    'text',
                    'text',
                  ]}
                  headings={[
                    'Time',
                    'Order',
                    'Revenue',
                    'Google Ads',
                    'Meta',
                    'GA4',
                    'TikTok',
                    'Pinterest',
                    'Snapchat',
                  ]}
                  rows={conversions.map((e) => [
                    // Imported historical orders land here too: show the ORDER's
                    // time, with its date whenever it was not placed today.
                    (() => {
                      const d = new Date(e.orderCreatedAt || e.createdAt);
                      const today = new Date().toDateString() === d.toDateString();
                      return today
                        ? d.toLocaleTimeString(localeMap[language])
                        : d.toLocaleString(localeMap[language], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
                    })(),
                    e.orderName || e.orderId,
                    `${e.revenue} ${e.currency}`,
                    e.googleAdsTracked ? '✓' : '—',
                    e.metaTracked ? '✓' : '—',
                    e.ga4Tracked ? '✓' : '—',
                    e.tiktokTracked ? '✓' : '—',
                    e.pinterestTracked ? '✓' : '—',
                    e.snapchatTracked ? '✓' : '—',
                  ])}
                />
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
